import { MAX_FILE_SHARE_BYTES, MAX_FILE_SHARE_CHUNKS, validFileShareMeta, validFileShareSize, } from './callFileShare';
export const FILE_SHARE_TOPIC = 'fileshare';
// Reliable data packets are capped around 15 KiB — stay safely below.
const CHUNK_SIZE = 14 * 1024;
// Pace bursts so the SFU data channel never starves media on slow uplinks.
const BURST_CHUNKS = 4;
const PACE_INTERVAL_MS = 30;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const encoder = new TextEncoder();
const decoder = new TextDecoder();
function frameControl(msg) {
    const body = encoder.encode(JSON.stringify(msg));
    const buf = new Uint8Array(body.length + 1);
    buf[0] = 1;
    buf.set(body, 1);
    return buf;
}
function frameChunk(chunk) {
    const buf = new Uint8Array(chunk.length + 1);
    buf[0] = 2;
    buf.set(chunk, 1);
    return buf;
}
/**
 * Stateful receiver: feed every DataReceived payload for FILE_SHARE_TOPIC.
 * Keeps one in-flight transfer per sender identity.
 */
export function createLiveKitFileReceiver(onUpdate) {
    const incoming = new Map();
    const fail = (from) => {
        const st = incoming.get(from);
        if (!st)
            return;
        incoming.delete(from);
        onUpdate({
            id: st.meta.id, name: st.meta.name, size: st.meta.size, mime: st.meta.mime,
            mode: st.meta.mode || 'download', from, direction: 'in',
            progress: st.received / Math.max(st.meta.size, 1), status: 'error',
        });
    };
    return {
        /** Sender left the call — abort its in-flight transfer, if any. */
        senderLeft: fail,
        handleData(payload, from) {
            if (payload.length < 1)
                return;
            if (payload[0] === 1) {
                let parsed;
                try {
                    parsed = JSON.parse(decoder.decode(payload.subarray(1)));
                }
                catch {
                    return;
                }
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
                    return;
                const msg = parsed;
                if (msg.t === 'meta') {
                    fail(from); // replacement meta must release the previous chunk array
                    if (!validFileShareMeta(msg)) {
                        if (typeof msg.id === 'string' && typeof msg.name === 'string' && typeof msg.mime === 'string' &&
                            typeof msg.size === 'number' && Number.isFinite(msg.size) && msg.size > MAX_FILE_SHARE_BYTES) {
                            onUpdate({
                                id: msg.id, name: msg.name, size: msg.size, mime: msg.mime, mode: 'download',
                                from, direction: 'in', progress: 0, status: 'error', errorReason: 'too_large',
                            });
                        }
                        return;
                    }
                    incoming.set(from, { meta: msg, chunks: [], received: 0 });
                    onUpdate({
                        id: msg.id, name: msg.name, size: msg.size, mime: msg.mime, mode: msg.mode || 'download',
                        from, direction: 'in', progress: 0, status: 'transferring',
                    });
                }
                else if (msg.t === 'done') {
                    const st = incoming.get(from);
                    if (!st || st.meta.id !== msg.id)
                        return;
                    incoming.delete(from);
                    if (st.received !== st.meta.size) {
                        onUpdate({
                            id: st.meta.id, name: st.meta.name, size: st.meta.size, mime: st.meta.mime,
                            mode: st.meta.mode || 'download', from, direction: 'in',
                            progress: st.received / Math.max(st.meta.size, 1), status: 'error',
                        });
                        return;
                    }
                    const blob = new Blob(st.chunks, { type: st.meta.mime || 'application/octet-stream' });
                    onUpdate({
                        id: st.meta.id, name: st.meta.name, size: st.meta.size, mime: st.meta.mime,
                        mode: st.meta.mode || 'download', from, direction: 'in',
                        progress: 1, status: 'done', blobUrl: URL.createObjectURL(blob),
                    });
                }
                else if (msg.t === 'abort') {
                    const st = incoming.get(from);
                    if (st && st.meta.id === msg.id)
                        fail(from);
                }
            }
            else if (payload[0] === 2) {
                const st = incoming.get(from);
                if (!st)
                    return;
                // strict byte budget: never accept more than the declared (capped) size
                const chunkSize = payload.length - 1;
                if (chunkSize === 0)
                    return;
                if (chunkSize > st.meta.size - st.received || st.chunks.length >= MAX_FILE_SHARE_CHUNKS) {
                    fail(from);
                    return;
                }
                const chunk = payload.subarray(1).slice(); // copy only after budget check; payload buffer is reused
                st.chunks.push(chunk);
                st.received += chunk.length;
                if (st.received === st.meta.size || st.received % (512 * 1024) < CHUNK_SIZE) {
                    onUpdate({
                        id: st.meta.id, name: st.meta.name, size: st.meta.size, mime: st.meta.mime,
                        mode: st.meta.mode || 'download', from, direction: 'in',
                        progress: st.received / Math.max(st.meta.size, 1), status: 'transferring',
                    });
                }
            }
        },
    };
}
/** Send a file to the given recipient identities via the room's data channel. */
export async function sendFileOverLiveKit(room, file, recipients, onUpdate, mode = 'download') {
    const id = `fs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const base = {
        id, name: file.name, size: file.size, mime: file.type, mode,
        from: '', direction: 'out',
    };
    if (!validFileShareSize(file.size)) {
        onUpdate({ ...base, progress: 0, status: 'error', errorReason: 'too_large' });
        return;
    }
    if (recipients.length === 0) {
        onUpdate({ ...base, progress: 0, status: 'error' });
        return;
    }
    const opts = { reliable: true, destinationIdentities: recipients, topic: FILE_SHARE_TOPIC };
    try {
        await room.localParticipant.publishData(frameControl({ t: 'meta', id, name: file.name, size: file.size, mime: file.type, mode }), opts);
        onUpdate({ ...base, progress: 0, status: 'transferring' });
        let offset = 0;
        let burst = 0;
        while (offset < file.size) {
            const chunk = new Uint8Array(await file.slice(offset, offset + CHUNK_SIZE).arrayBuffer());
            await room.localParticipant.publishData(frameChunk(chunk), opts);
            offset += chunk.length;
            if (offset === file.size || offset % (512 * 1024) < CHUNK_SIZE) {
                onUpdate({ ...base, progress: offset / Math.max(file.size, 1), status: 'transferring' });
            }
            burst += 1;
            if (burst >= BURST_CHUNKS && offset < file.size) {
                burst = 0;
                await sleep(PACE_INTERVAL_MS);
            }
        }
        await room.localParticipant.publishData(frameControl({ t: 'done', id }), opts);
        // The sender keeps a local object URL too, so the admin sees (and can
        // re-open) exactly what was shared — mirrors the receivers' view.
        onUpdate({ ...base, progress: 1, status: 'done', blobUrl: URL.createObjectURL(file) });
    }
    catch (err) {
        console.error('[lkFileShare] send failed:', err);
        try {
            await room.localParticipant.publishData(frameControl({ t: 'abort', id }), opts);
        }
        catch { /* ignore */ }
        onUpdate({ ...base, progress: 0, status: 'error' });
    }
}
